import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import SUTextField from '../../Components/SUTextField';
import NodeEditorChrome from '../../Components/NodeEditorChrome';
import TriggerTypeField from '../../Components/TriggerTypeField';
import { InfoCallout } from '../../Components/NodeFormKit';
import useNodeEditor from '../../Components/useNodeEditor';

import styledComponent from 'styled-components';
import Select from 'react-select';

import { FiMessageSquare } from 'react-icons/fi';

const TRANSFER_NODE_COLOR = '#F56F89';

const TransferAgent = ({
  selectedNode,
  handleSelectedNode,
  removeNode,
  nodes,
  edges,
  editFlow,
  updateBot,
  truncateText,
  customStyles,
  customStylesDark,
  isDark,
  listAreas,
  listAdvisers,
  watchSelectedNode,
  valueSelectedVariable,
  typeInputValue,
  typeInput,
  resetVariable,
  contextVariables,
  onMouseDown,
  isDragging,
  isExpanded,
  setIsExpanded,
}) => {
  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
  } = useForm({
    user_response: null,
  });

  const { handleInputChange, loadData, saveNode, getIsDirty } = useNodeEditor({
    selectedNode,
    handleSelectedNode,
    nodes,
    edges,
    editFlow,
    updateBot,
    reset,
    getValues,
  });

  const [errorAgent, setErrorAgent] = useState(false);

  // ------> Selects, Radios, Checkbox
  const [loadingUpdate, setLoadingUpdate] = useState(false);

  // FUNCTIONS

  //# Function para manejar cambios de estados de campos

  //# Function para guardar los datos
  const saveData = (values) => {
    if (!selectedNode.data?.agent_area?.value) {
      setErrorAgent(true);
      return;
    }

    setLoadingUpdate(true);

    const data = {
      title: values.title,
      user_response: values.user_response,
      bot_response: values.bot_response,
      trigger_type: selectedNode.data?.trigger_type || 'message',
    };

    saveNode(data, () => {
      setLoadingUpdate(false);
    });
  };

  //# Function para obtner data
  const getData = () => {
    let objectData = {
      title: selectedNode.data?.title ?? null,
      user_response: selectedNode.data?.user_response ?? null,
      bot_response: selectedNode.data?.bot_response ?? null,
      trigger_type: selectedNode.data?.trigger_type ?? 'message',
    };
    loadData(objectData);
  };

  useEffect(() => {
    if (typeInput == 'node') {
      setValue(
        typeInputValue,
        getValues(typeInputValue) + valueSelectedVariable
      );
      resetVariable();
    }
  }, [valueSelectedVariable]);

  useEffect(() => {
    getData();
  }, [watchSelectedNode]);

  return (
    <NodeEditorChrome
      icon={FiMessageSquare}
      color={TRANSFER_NODE_COLOR}
      typeName="Transferir Agente"
      title={truncateText(selectedNode.data.title)}
      onRemove={() => removeNode(selectedNode.id)}
      onMouseDown={onMouseDown}
      isDragging={isDragging}
      onClose={() => handleSelectedNode(null)}
      formId="transferAgentForm"
      saving={loadingUpdate}
      isDark={isDark}
      getIsDirty={getIsDirty}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
    >
        <form id="transferAgentForm" onSubmit={handleSubmit(saveData)} style={{ width: '100%' }}>
          <div>
            <div className="col p-0">
              <SUTextField
                id="title"
                name="title"
                label="Título:"
                type="text"
                selectedNode={selectedNode}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                {...register('title', {
                  required: {
                    value: true,
                    message: 'Campo requerido.',
                  },
                  pattern: {
                    value: /\S+/,
                    message: 'No puede ser solo espacios en blanco',
                  },
                })}
              />
            </div>
            {!selectedNode.data?.first && (<>
            <div className="col p-0 mt-4">
              <SUTextField
                id="user_response"
                name="user_response"
                label="Respuesta del cliente:"
                hint="Lo que debe llegar para activar este nodo: el texto que escribe el cliente, el título de un botón, o * para cualquier respuesta. Para varios, separalos con  /  ."
                type="textArea"
                selectedNode={selectedNode}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                {...register('user_response', {
                  required: {
                    value: true,
                    message: 'Campo requerido.',
                  },
                  pattern: {
                    value: /\S+/,
                    message: 'No puede ser solo espacios en blanco',
                  },
                })}
              />
            </div>
            <div className="col p-0 mt-4">
              <TriggerTypeField
                value={selectedNode.data?.trigger_type}
                handleInputChange={handleInputChange}
                isDark={isDark}
                customStyles={customStyles}
                customStylesDark={customStylesDark}
              />
            </div>
            </>)}
            <div className="col p-0 mt-4">
              <SUTextField
                id="bot_response"
                name="bot_response"
                label="Respuesta del Bot:"
                maxLength={4096}
                placeholder="Escribe la respuesta del Bot"
                type="textArea"
                showEmoji
                showSticker
                showVariables
                selectedNode={selectedNode}
                contextVariables={contextVariables}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                {...register('bot_response', {
                  required: {
                    value: true,
                    message: 'Campo requerido.',
                  },
                  pattern: {
                    value: /\S+/,
                    message: 'No puede ser solo espacios en blanco',
                  },
                })}
              />

              <InfoCallout title="Importante:">
                Las transferencias a agentes o áreas se realizan únicamente
                durante el horario comercial. El cliente recibirá el mensaje
                configurado en este apartado.
              </InfoCallout>
            </div>

            <div className="col p-0 mt-4">
              <div className="su-seg3" role="group" aria-label="Transferir a">
                {[
                  { value: 'area', label: 'Áreas' },
                  { value: 'agent', label: 'Agentes' },
                  { value: 'automatic', label: 'Automático' },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={selectedNode.data.agent_area?.type === option.value}
                    className={selectedNode.data.agent_area?.type === option.value ? 'on' : ''}
                    onClick={() =>
                      handleInputChange('agent_area', {
                        label: null,
                        value: null,
                        type: option.value,
                      })
                    }
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {selectedNode.data.agent_area?.type == 'area' && (
                <div>
                  <label>Areas: </label>
                  <div className="su-control-group d-flex">
                    <Select
                      styles={
                        isDark == 'true' ? customStylesDark : customStyles
                      }
                      className="form-control basic-multi-select"
                      placeholder="Seleccione..."
                      name="areas"
                      value={selectedNode.data.agent_area || null}
                      onChange={(area) => {
                        handleInputChange('agent_area', area);
                        setErrorAgent(false);
                      }}
                      options={listAreas}
                      classNamePrefix="select"
                      noOptionsMessage={() => 'Sin opciones'}
                      getOptionLabel={(option) => (
                        <div
                          className="option d-flex"
                          style={{ fontSize: '15px' }}
                        >
                          {option.label}
                        </div>
                      )}
                      getOptionValue={(option) => option.value}
                    />
                  </div>
                </div>
              )}
              {selectedNode.data.agent_area?.type == 'agent' && (
                <div>
                  <label>Agentes: </label>
                  <div className="su-control-group d-flex">
                    <Select
                      styles={
                        isDark == 'true' ? customStylesDark : customStyles
                      }
                      className="form-control basic-multi-select"
                      placeholder="Seleccione..."
                      name="agents"
                      value={selectedNode.data.agent_area || null}
                      onChange={(agent) => {
                        handleInputChange('agent_area', agent);
                        setErrorAgent(false);
                      }}
                      options={listAdvisers}
                      classNamePrefix="select"
                      noOptionsMessage={() => 'Sin opciones'}
                      getOptionLabel={(option) => (
                        <div
                          className="option d-flex"
                          style={{ fontSize: '15px' }}
                        >
                          {option.label}
                        </div>
                      )}
                      getOptionValue={(option) => option.value}
                    />
                  </div>
                </div>
              )}
              {selectedNode.data.agent_area?.type == 'automatic' && (
                <div>
                  <label>Automático: </label>
                  <div className="su-control-group d-flex">
                    <Select
                      styles={
                        isDark == 'true' ? customStylesDark : customStyles
                      }
                      className="form-control basic-multi-select"
                      placeholder="Seleccione..."
                      name="automatics"
                      value={selectedNode.data.agent_area || null}
                      onChange={(auto) => {
                        handleInputChange('agent_area', auto);
                        setErrorAgent(false);
                      }}
                      options={[
                        {
                          label: 'Area',
                          id: 'areas',
                          type: 'automatic',
                        },
                        {
                          label: 'Agente',
                          id: 'agents',
                          type: 'automatic',
                        },
                      ]}
                      classNamePrefix="select"
                      noOptionsMessage={() => 'Sin opciones'}
                      getOptionLabel={(option) => (
                        <div
                          className="option d-flex"
                          style={{ fontSize: '15px' }}
                        >
                          {option.label}
                        </div>
                      )}
                      getOptionValue={(option) => option.id}
                    />
                  </div>
                </div>
              )}
              {errorAgent && <Alert>* Campo requerido</Alert>}
            </div>
          </div>
        </form>
    </NodeEditorChrome>
  );
};

export default TransferAgent;

const Alert = styledComponent.span`
  color: red;
  display: block;
  width: 100%;
  // width: 200px;
`;
